import { useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import {
  createMtrStructSub,
  fetchMtrStructSubDetail,
  updateMtrStructSub,
} from '#/api/imsMgmt/settings/mtrStruct';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { mtrStructSubFormSchema } from '#/modules/imsMgmt/settings/mtrStruct/schemas/mtrStructSchemas';
import {
  getMtrStructApiErrorMessage,
  isMtrStructVersionConflict,
} from '#/modules/imsMgmt/settings/mtrStruct/helper/apiError';
import { MTR_STRUCT_SUB_FIELD_LABELS } from '#/modules/imsMgmt/settings/mtrStruct/helper/formValidationLabels';
import { fetchMtrStructMainSelectOptions } from '#/modules/imsMgmt/settings/mtrStruct/helper/mainSelectOptions';
import {
  mtrStructSubDetailQueryKey,
  mtrStructSubListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreateEditMtrStructSubPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const updatedAtRef = useRef(null);
  const presetMainId = searchParams.get('mtrStructMainId') || '';
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const mainOptionsQuery = useQuery({
    queryKey: ['erp', 'mtrStructMain', 'selectOptions'],
    queryFn: fetchMtrStructMainSelectOptions,
  });

  const detailQuery = useQuery({
    queryKey: mtrStructSubDetailQueryKey(id),
    queryFn: () => fetchMtrStructSubDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createMtrStructSub,
    updateFn: updateMtrStructSub,
    buildPayload: (values) => {
      const payload = {
        mtrStructMainId: values.mtrStructMainId,
        code: values.code,
        name: values.name,
        remark: values.remark || null,
      };

      if (id) {
        return {
          ...payload,
          updatedAt: updatedAtRef.current,
        };
      }

      return payload;
    },
    listQueryKeyPrefix: mtrStructSubListQueryKeyPrefix,
    detailQueryKey: mtrStructSubDetailQueryKey,
    getEditPath: ROUTES.mtrStructSubEdit,
    getErrorMessage: (error) => getMtrStructApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isMtrStructVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(mtrStructSubFormSchema),
    defaultValues: {
      mtrStructMainId: presetMainId,
      code: '',
      name: '',
      remark: '',
    },
  });

  useEffect(() => {
    if (isEdit) {
      return;
    }

    if (presetMainId) {
      setValue('mtrStructMainId', presetMainId, { shouldValidate: true });
    }
  }, [isEdit, presetMainId, setValue]);

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset({
      mtrStructMainId: detail.mtrStructMainId ?? detail.mtrStructMain?.id ?? '',
      code: detail.code ?? '',
      name: detail.name ?? '',
      remark: detail.remark ?? '',
    });
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯子分類' : '新增子分類';
  const isPageLoading = (isEdit && detailQuery.isLoading) || mainOptionsQuery.isLoading;
  const mainOptions = mainOptionsQuery.data ?? [];

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-lg">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.mtrStruct}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={MTR_STRUCT_SUB_FIELD_LABELS}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body">
                  <FloatingSelect
                    id="mtrStructMainId"
                    label="主分類"
                    options={mainOptions}
                    control={control}
                    errors={errors}
                  />
                  <FloatingInput
                    id="code"
                    label="代號"
                    register={register}
                    errors={errors}
                  />
                  <FloatingInput
                    id="name"
                    label="名稱"
                    register={register}
                    errors={errors}
                  />
                  <FloatingTextarea
                    id="remark"
                    label="備註"
                    register={register}
                    errors={errors}
                  />
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.mtrStruct}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditMtrStructSubPage;
